import React, { useCallback } from 'react';
import loadable from '@loadable/component';
import { CarCard } from '@perxis-auto/ui-kit/lib/components/CarCard';

import { useConstructor } from '@/context/constructor';
import useCarData from '@/features/cars/hooks/useCarData';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import {
  ConstructorElementType,
  ConstructorEnum,
} from '@/interfaces/constructor.interfaces';
import ConstructorGrid from '@/layouts/Constructor/components/ConstructorGrid';

import { CardProps } from './Card.types';

import styles from './Card.module.scss';

const CardImage = loadable(() => import('@/blocks/card/CardImage'));
const CardLocation = loadable(() => import('@/blocks/card/CardLocation'));
const CardMarkers = loadable(() => import('@/blocks/card/CardMarkers'));
const CardPayment = loadable(() => import('@/blocks/card/CardPayment'));
const CardPrice = loadable(() => import('@/blocks/card/CardPrice'));
const CardTable = loadable(() => import('@/blocks/card/CardTable'));
const CardTitle = loadable(() => import('@/blocks/card/CardTitle'));
const CardBookingButton = loadable(() => import('@/blocks/card/CardBookingButton'));
const CardActions = loadable(() => import('@/blocks/card/CardActions'));
const Button = loadable(() => import('@/blocks/common/Button'));
const Custom = loadable(() => import('@/blocks/common/Custom'));

/**
 * Блок карточки основной информации об автомобиле.
 * Используется на странице автомобиля.
 */
function Card(props: CardProps) {
  const { data: extendedCarItem = null, isLoading } = useCarData();

  const { grid: carCardGrid } = useConstructor();

  const locationIds = useAppSelector(rootSelectors.config.locationIdsSelector);

  const isCardHidden = (!isLoading && !extendedCarItem) || !carCardGrid;

  const renderItem = useCallback(
    (item: ConstructorElementType) => {
      const { id, type, props } = item;
      switch (type) {
        case ConstructorEnum.CarCardImages:
          return (
            <CardImage key={id} data={extendedCarItem} isLoading={isLoading} {...props} />
          );
        case ConstructorEnum.CarCardTitle:
          return (
            <CardTitle key={id} data={extendedCarItem} isLoading={isLoading} {...props} />
          );
        case ConstructorEnum.CarCardMarkers:
          return (
            <CardMarkers
              key={id}
              data={extendedCarItem}
              isLoading={isLoading}
              {...props}
            />
          );
        case ConstructorEnum.CarCardSpecification:
          return (
            <CardTable key={id} data={extendedCarItem} isLoading={isLoading} {...props} />
          );
        case ConstructorEnum.CarCardPrice:
          return (
            <CardPrice key={id} data={extendedCarItem} isLoading={isLoading} {...props} />
          );
        case ConstructorEnum.CarCardPayment:
          return (
            <CardPayment
              key={id}
              data={extendedCarItem}
              isLoading={isLoading}
              {...props}
            />
          );
        case ConstructorEnum.CarCardLocation:
          return (
            <CardLocation
              key={id}
              data={extendedCarItem}
              isLoading={isLoading}
              locationIds={locationIds}
              {...props}
            />
          );
        case ConstructorEnum.CarCardBooking: {
          return (
            <CardBookingButton
              key={id}
              data={extendedCarItem}
              isLoading={isLoading}
              {...props}
            />
          );
        }
        case ConstructorEnum.CarCardActions:
          return <CardActions data={extendedCarItem} isLoading={isLoading} {...props} />;
        case ConstructorEnum.Custom:
          return <Custom id={id} key={id} {...props} />;
        case ConstructorEnum.Button:
          return (
            <Button
              id={id}
              key={id}
              data={extendedCarItem}
              isLoading={isLoading}
              {...props}
            />
          );
        default:
          return null;
      }
    },
    [extendedCarItem, isLoading, locationIds],
  );

  if (isCardHidden) {
    return null;
  }

  return (
    <div className={styles.card}>
      <CarCard>
        <ConstructorGrid skipFirstSubGrid grid={carCardGrid} renderItem={renderItem} />
      </CarCard>
    </div>
  );
}

export default Card;
